/**
 * Main Application Entry Point - Maria's Tic-Tac-Toe
 * Gaming Edition with Modal Support
 */

import './styles/main.css';
import { GameStateManager } from './core/GameStateManager';
import { StorageService } from './services/StorageService';
import { PlayerManager } from './services/PlayerManager';
import { GameBoard } from './ui/GameBoard';
import type { GameState } from './types';

/**
 * Application class - Main controller
 */
class App {
  private stateManager: GameStateManager;
  private storageService: StorageService;
  private playerManager: PlayerManager;
  private gameBoard: GameBoard | null = null;
  private gameStartTime: number = Date.now();
  private focusedCell: { row: number; col: number } = { row: 1, col: 1 }; // Keyboard navigation state

  constructor() {
    // Initialize services
    this.storageService = new StorageService();
    this.playerManager = new PlayerManager(this.storageService);
    this.stateManager = new GameStateManager();

    // Initialize UI
    this.initializeGameBoard();
    this.setupEventListeners();
    
    // Setup keyboard navigation
    this.setupKeyboardNavigation();

    // Subscribe to state changes
    this.stateManager.subscribe((state) => this.onStateChange(state));

    // Initial render
    this.updateScoreboard();
    this.updateCurrentTurn();
  }

  /**
   * Initialize Konva.js game board
   */
  private initializeGameBoard(): void {
    const container = document.getElementById('game-board-container');
    if (!container) {
      console.error('Game board container not found');
      return;
    }

    // Calculate size based on available container space
    const size = Math.min(container.clientWidth, container.clientHeight);

    this.gameBoard = new GameBoard({
      container: 'game-board-container',
      width: size,
      height: size,
      onCellClick: (row, col) => this.handleCellClick(row, col),
    });

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
  }

  /**
   * Setup event listeners for UI controls
   */
  private setupEventListeners(): void {
    // Player name inputs
    const playerXInput = document.getElementById('player-x-name') as HTMLInputElement;
    const playerOInput = document.getElementById('player-o-name') as HTMLInputElement;

    playerXInput?.addEventListener('blur', () => {
      this.playerManager.updatePlayerName('X', playerXInput.value);
      this.updateCurrentTurn();
    });

    playerOInput?.addEventListener('blur', () => {
      this.playerManager.updatePlayerName('O', playerOInput.value);
      this.updateCurrentTurn();
    });

    // Game controls
    document.getElementById('reset-game')?.addEventListener('click', () => {
      this.resetGame();
    });

    document.getElementById('reset-scores')?.addEventListener('click', () => {
      if (confirm('Are you sure you want to reset all scores?')) {
        this.playerManager.resetScores();
        this.updateScoreboard();
      }
    });

    // Help button
    document.getElementById('show-help')?.addEventListener('click', () => {
      this.showHelpModal();
    });

    // Modal controls
    document.getElementById('modal-new-game')?.addEventListener('click', () => {
      this.closeModal();
      this.resetGame();
    });

    document.getElementById('modal-close')?.addEventListener('click', () => {
      this.closeModal();
    });

    // Help modal close
    document.getElementById('help-close')?.addEventListener('click', () => {
      this.closeHelpModal();
    });

    // Close modal on overlay click
    document.getElementById('game-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'game-modal') {
        this.closeModal();
      }
    });

    // Close help modal on overlay click
    document.getElementById('help-modal')?.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).id === 'help-modal') {
        this.closeHelpModal();
      }
    });
  }


  /**
   * Setup keyboard navigation for accessibility
   */
  private setupKeyboardNavigation(): void {
    const boardContainer = document.getElementById('game-board-container');
    if (!boardContainer) return;

    // Add escape key to close modals
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeModal();
        this.closeHelpModal();
        return;
      }
    });

    boardContainer.addEventListener('keydown', (e: KeyboardEvent) => {
      const state = this.stateManager.getState();
      
      // Don't handle if game is over
      if (state.gameStatus !== 'playing') {
        if (e.key === 'r' || e.key === 'R') {
          this.resetGame();
          this.announceToScreenReader('Game reset. Player X starts.');
        }
        return;
      }

      let handled = false;

      switch (e.key) {
        case 'ArrowUp':
          this.focusedCell.row = Math.max(0, this.focusedCell.row - 1);
          handled = true;
          break;
        case 'ArrowDown':
          this.focusedCell.row = Math.min(2, this.focusedCell.row + 1);
          handled = true;
          break;
        case 'ArrowLeft':
          this.focusedCell.col = Math.max(0, this.focusedCell.col - 1);
          handled = true;
          break;
        case 'ArrowRight':
          this.focusedCell.col = Math.min(2, this.focusedCell.col + 1);
          handled = true;
          break;
        case 'Enter':
        case ' ':
          this.handleCellClick(this.focusedCell.row, this.focusedCell.col);
          handled = true;
          break;
        case 'r':
        case 'R':
          this.resetGame();
          this.announceToScreenReader('Game reset. Player X starts.');
          handled = true;
          break;
      }

      if (handled) {
        e.preventDefault();
        
        // Announce focused cell to screen reader
        if (e.key.startsWith('Arrow')) {
          const cellValue = state.board[this.focusedCell.row]?.[this.focusedCell.col];
          const cellContent = cellValue || 'empty';
          const position = `Row ${this.focusedCell.row + 1}, Column ${this.focusedCell.col + 1}`;
          this.announceToScreenReader(`${position}, ${cellContent}`);
          
          // Visual feedback (could add a focus indicator on canvas)
          this.updateFocusIndicator();
        }
      }
    });
  }

  /**
   * Show modal with game result
   */
  private showModal(emoji: string, title: string, message: string): void {
    const modal = document.getElementById('game-modal');
    const modalEmoji = document.getElementById('modal-emoji');
    const modalTitle = document.getElementById('modal-title');
    const modalMessage = document.getElementById('modal-message');

    if (modal && modalEmoji && modalTitle && modalMessage) {
      modalEmoji.textContent = emoji;
      modalTitle.textContent = title;
      modalMessage.textContent = message;
      modal.classList.add('active');
      
      // Focus the modal for accessibility
      const closeButton = document.getElementById('modal-close');
      closeButton?.focus();
    }
  }

  /**
   * Close the modal
   */
  private closeModal(): void {
    const modal = document.getElementById('game-modal');
    if (modal) {
      modal.classList.remove('active');
      
      // Return focus to game board
      const boardContainer = document.getElementById('game-board-container');
      boardContainer?.focus();
    }
  }

  /**
   * Show help modal
   */
  private showHelpModal(): void {
    const modal = document.getElementById('help-modal');
    if (modal) {
      modal.classList.add('active');
    }
  }

  /**
   * Close help modal
   */
  private closeHelpModal(): void {
    const modal = document.getElementById('help-modal');
    if (modal) {
      modal.classList.remove('active');
      
      // Return focus to game board
      const boardContainer = document.getElementById('game-board-container');
      boardContainer?.focus();
    }
  }

  /**
   * Update visual focus indicator on canvas
   */
  private updateFocusIndicator(): void {
    // This would require adding a visual focus indicator to the GameBoard
    // For now, we rely on screen reader announcements
    // TODO: Add visual focus ring to GameBoard.ts
  }

  /**
   * Announce message to screen readers
   */
  private announceToScreenReader(message: string): void {
    const announcer = document.getElementById('sr-announcements');
    if (announcer) {
      announcer.textContent = message;
      
      // Clear after announcement to allow repeat announcements
      setTimeout(() => {
        announcer.textContent = '';
      }, 1000);
    }
  }

  /**
   * Handle cell click
   */
  private handleCellClick(row: number, col: number): void {
    const state = this.stateManager.getState();
    
    if (state.gameStatus !== 'playing') {
      return;
    }

    // Check if cell is already occupied
    const cellValue = state.board[row]?.[col];
    if (cellValue) {
      this.announceToScreenReader('Cell already occupied. Choose another cell.');
      return;
    }

    const currentPlayer = state.currentPlayer;
    this.stateManager.makeMove(row, col);
    
    // Announce move to screen reader (state will have changed after makeMove)
    const nextState = this.stateManager.getState();
    if (nextState.gameStatus === 'playing') {
      const nextPlayerProfile = this.playerManager.getPlayerProfile(nextState.currentPlayer);
      this.announceToScreenReader(
        `${currentPlayer} placed at row ${row + 1}, column ${col + 1}. ${nextPlayerProfile.name}'s turn.`
      );
    }
  }

  /**
   * Handle state changes
   */
  private onStateChange(state: GameState): void {
    // Update board rendering
    this.gameBoard?.render(state);

    // Update UI
    this.updateCurrentTurn();

    // Handle game end
    if (state.gameStatus === 'won' || state.gameStatus === 'draw') {
      this.handleGameEnd(state);
    }
  }

  /**
   * Handle game end (win or draw)
   */
  private handleGameEnd(state: GameState): void {
    const duration = Math.floor((Date.now() - this.gameStartTime) / 1000);
    const statusElement = document.getElementById('game-status');

    if (state.gameStatus === 'won' && state.winner) {
      const winnerProfile = this.playerManager.getPlayerProfile(state.winner);
      
      // Show inline status
      if (statusElement) {
        statusElement.textContent = `🎉 ${winnerProfile.name} wins!`;
        statusElement.className = 'game-status win';
      }
      
      // Show modal with celebration
      this.showModal(
        '🏆',
        'Victory!',
        `${winnerProfile.name} wins the game! Great job!`
      );
      
      // Announce win to screen reader
      this.announceToScreenReader(`Game over! ${winnerProfile.name} wins! Press R to play again.`);

      // Record game result
      this.playerManager.recordGameResult({
        gameId: crypto.randomUUID(),
        winner: state.winner,
        completedAt: new Date(),
        duration,
        moveHistory: [],
      });
    } else if (state.gameStatus === 'draw') {
      // Show inline status
      if (statusElement) {
        statusElement.textContent = "🤝 It's a draw!";
        statusElement.className = 'game-status draw';
      }
      
      // Show modal
      this.showModal(
        '🤝',
        "It's a Draw!",
        'Well played! Both players showed great skill!'
      );
      
      // Announce draw to screen reader
      this.announceToScreenReader("Game over! It's a draw! Press R to play again.");

      // Record draw
      this.playerManager.recordGameResult({
        gameId: crypto.randomUUID(),
        winner: 'draw',
        completedAt: new Date(),
        duration,
        moveHistory: [],
      });
    }

    // Update scoreboard
    this.updateScoreboard();
  }

  /**
   * Reset game to initial state
   */
  private resetGame(): void {
    this.stateManager.reset();
    this.gameBoard?.clear();
    this.gameStartTime = Date.now();
    this.focusedCell = { row: 1, col: 1 }; // Reset focus to center

    const statusElement = document.getElementById('game-status');
    if (statusElement) {
      statusElement.textContent = '';
      statusElement.className = 'game-status';
    }

    this.updateCurrentTurn();
    
    // Refocus the board container for keyboard navigation
    const boardContainer = document.getElementById('game-board-container');
    boardContainer?.focus();
  }

  /**
   * Update scoreboard display
   */
  private updateScoreboard(): void {
    const playerX = this.playerManager.getPlayerProfile('X');
    const playerO = this.playerManager.getPlayerProfile('O');

    // Update Player X scores
    this.updateElement('player-x-wins', playerX.wins.toString());
    this.updateElement('player-x-losses', playerX.losses.toString());
    this.updateElement('player-x-draws', playerX.draws.toString());

    // Update Player O scores
    this.updateElement('player-o-wins', playerO.wins.toString());
    this.updateElement('player-o-losses', playerO.losses.toString());
    this.updateElement('player-o-draws', playerO.draws.toString());

    // Update player names
    const playerXInput = document.getElementById('player-x-name') as HTMLInputElement;
    const playerOInput = document.getElementById('player-o-name') as HTMLInputElement;
    if (playerXInput) playerXInput.value = playerX.name;
    if (playerOInput) playerOInput.value = playerO.name;
  }

  /**
   * Update current turn indicator
   */
  private updateCurrentTurn(): void {
    const state = this.stateManager.getState();
    const currentPlayer = state.currentPlayer;
    const playerProfile = this.playerManager.getPlayerProfile(currentPlayer);

    const turnElement = document.getElementById('current-turn-text');
    if (turnElement) {
      if (state.gameStatus === 'playing') {
        turnElement.textContent = `${playerProfile.name}'s Turn`;
      } else {
        turnElement.textContent = 'Game Over';
      }
    }
  }

  /**
   * Handle window resize
   */
  private handleResize(): void {
    const container = document.getElementById('game-board-container');
    if (!container || !this.gameBoard) return;

    // Calculate size based on available container space (no fixed max)
    const size = Math.min(container.clientWidth, container.clientHeight);
    this.gameBoard.resize(size, size);
    this.gameBoard.render(this.stateManager.getState());
  }

  /**
   * Helper to update element text content
   */
  private updateElement(id: string, value: string): void {
    const element = document.getElementById(id);
    if (element) {
      element.textContent = value;
    }
  }
}

// Initialize app when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new App());
} else {
  new App();
}
